import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowLeftIcon, CheckIcon, MailIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  invitationsQuery,
  useCreateInvitation,
  useDeleteInvitation,
  useInvitations,
  usersQuery,
  useUsers,
} from "@/features/auth/admin-queries";
import { UserMenu } from "@/features/auth/UserMenu";

export const Route = createFileRoute("/_authed/people")({
  beforeLoad: ({ context }) => {
    if (context.user.role !== "admin") throw redirect({ to: "/" });
  },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(usersQuery()),
      context.queryClient.ensureQueryData(invitationsQuery()),
    ]),
  component: PeoplePage,
});

function PeoplePage() {
  const { data: users = [] } = useUsers();
  const { data: invitations = [] } = useInvitations();
  const create = useCreateInvitation();
  const remove = useDeleteInvitation();
  const [email, setEmail] = useState("");
  const pending = invitations.filter((i) => !i.acceptedAt);

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    create.mutate(value, { onSuccess: () => setEmail("") });
  };

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-8 overflow-y-auto px-6 py-10">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon-sm" aria-label="Back to roadmaps">
            <Link to="/">
              <ArrowLeftIcon />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">People</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign-up is invite-only. Add an email here, then send that person the sign-up link.
            </p>
          </div>
        </div>
        <UserMenu />
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Invite someone
        </h2>
        <form onSubmit={invite} className="flex gap-2" data-testid="invite-form">
          <Input
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="invite-email"
            required
          />
          <Button type="submit" disabled={create.isPending} data-testid="invite-submit">
            <MailIcon data-icon="inline-start" />
            Invite
          </Button>
        </form>
        <p className="text-xs text-muted-foreground">
          Sign-up link:{" "}
          <code className="rounded bg-muted px-1 py-0.5">{window.location.origin}/signup</code>.
          Invited people can use their password or Google with that email.
        </p>
        {pending.length > 0 && (
          <ul className="divide-y rounded-lg border" data-testid="invitation-list">
            {pending.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                data-testid="invitation-row"
              >
                <span className="truncate">{inv.email}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    invited {format(new Date(inv.createdAt), "d MMM")}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove invitation for ${inv.email}`}
                    onClick={() => remove.mutate(inv.id)}
                    data-testid="invitation-remove"
                  >
                    <XIcon />
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Members ({users.length})
        </h2>
        <ul className="divide-y rounded-lg border" data-testid="user-list">
          {users.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{u.name}</span>
                <span className="truncate text-xs text-muted-foreground">{u.email}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  joined {format(new Date(u.createdAt), "d MMM yyyy")}
                </span>
                <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                  {u.role === "admin" && <CheckIcon />}
                  {u.role}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
